<template>
  <div class="LandingPageWrapper">
    <NavBar />
    <ContactPage v-if="$route.path === '/contact'" />
    <PrivacyPolicyPage v-else-if="$route.path === '/privacy'" />
    <TermsPage v-else-if="$route.path === '/terms'" />
    <HomePage v-else />
    <AppFooter />
  </div>
</template>

<script>
  import NavBar from './NavBar.vue'
  import HomePage from './HomePage.vue'
  import ContactPage from './ContactPage.vue'
  import PrivacyPolicyPage from './PrivacyPolicyPage.vue'
  import TermsPage from './TermsPage.vue'
  import AppFooter from './AppFooter.vue'

  export default {
    name: 'LandingPage',
    components: { NavBar, HomePage, ContactPage, PrivacyPolicyPage, TermsPage, AppFooter },
    data: () => ({
      scrollTargets: { '/features': 'features', '/how': 'how-it-works' },
    }),
    watch: {
      '$route.path': {
        immediate: true,
        handler(path) {
          const hash = this.scrollTargets[path]
          this.$nextTick(() => {
            if (hash) {
              document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
            } else {
              window.scrollTo({ top: 0, behavior: 'instant' })
            }
          })
        }
      }
    }
  }
</script>

<style>
/* ─── True globals: variables, reset, base elements ────────────────── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --primary:        #5B8DB8;
  --primary-light:  #7AADD4;
  --primary-dark:   #3D6A90;
  --secondary:      #7BAF96;
  --secondary-light:#A3CAB5;
  --accent:         #E8B4A0;
  --accent-dark:    #D4917A;
  --bg:             #F5F2EE;
  --bg-alt:         #EDF1F7;
  --bg-card:        #FFFFFF;
  --text:           #2D3748;
  --text-muted:     #718096;
  --text-light:     #A0AEC0;
  --border:         #E2E8F0;
  --shadow-sm:      0 2px 8px rgba(45, 55, 72, 0.06);
  --shadow-md:      0 8px 24px rgba(45, 55, 72, 0.10);
  --shadow-lg:      0 20px 48px rgba(45, 55, 72, 0.12);
  --radius-sm:      8px;
  --radius-md:      14px;
  --radius-lg:      22px;
  --radius-xl:      32px;
  --transition:     0.25s ease;
}

.LandingPageWrapper a { color: inherit; text-decoration: none; }
.LandingPageWrapper img { display: block; max-width: 100%; }
</style>

<style scoped>
/* ─── Section Base ─────────────────────────────────────────────────── */
:deep(section) { padding: 96px 2rem; }

:deep(.container) { max-width: 1100px; margin: 0 auto; }

:deep(.section-label) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(123, 175, 150, 0.10);
  color: #3a7a5a;
  border: 1px solid rgba(123, 175, 150, 0.25);
  padding: 0.3rem 0.9rem;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 1rem;
}

:deep(.section-title) {
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
  color: var(--text);
  max-width: 560px;
}

:deep(.section-title.centered) { max-width: 600px; margin: 0 auto; text-align: center; }

:deep(.section-desc) {
  margin-top: 1rem;
  font-size: 1.05rem;
  color: var(--text-muted);
  max-width: 480px;
  line-height: 1.7;
}

:deep(.section-desc.centered) { max-width: 520px; margin: 1rem auto 0; text-align: center; }

/* ─── Shared Buttons ───────────────────────────────────────────────── */
:deep(.btn-primary) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.6rem 1.3rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: var(--radius-sm);
  border: none;
  cursor: pointer;
  transition: opacity var(--transition), transform var(--transition), box-shadow var(--transition);
  box-shadow: 0 2px 10px rgba(91, 141, 184, 0.35);
}

:deep(.btn-primary:hover) {
  opacity: 0.92;
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(91, 141, 184, 0.45);
}

:deep(.btn-primary svg) { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2.5; }

:deep(.btn-large) {
  padding: 0.9rem 2rem;
  font-size: 1rem;
  border-radius: var(--radius-md);
  box-shadow: 0 4px 18px rgba(91, 141, 184, 0.40);
}

:deep(.btn-outline) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.9rem 2rem;
  background: transparent;
  color: var(--primary);
  font-size: 1rem;
  font-weight: 600;
  border-radius: var(--radius-md);
  border: 1.5px solid var(--primary);
  cursor: pointer;
  transition: background var(--transition), transform var(--transition);
}

:deep(.btn-outline:hover) { background: rgba(91, 141, 184, 0.06); transform: translateY(-1px); }

/* ─── Animations ────────────────────────────────────────────────────── */
:deep(.fade-up) {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}

:deep(.fade-up.visible) { opacity: 1; transform: translateY(0); }

:deep(.fade-up-delay-1) { transition-delay: 0.1s; }
:deep(.fade-up-delay-2) { transition-delay: 0.2s; }
:deep(.fade-up-delay-3) { transition-delay: 0.3s; }

/* ─── Responsive ────────────────────────────────────────────────────── */
@media (max-width: 700px) {
  :deep(section) { padding: 72px 1.25rem; }
}
</style>
